/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */
import React, { Component, cloneElement, createRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import withToggle from 'dpl/decorators/withToggle';
import {
  PHOTO_ALBUM_VERTICAL_PADDING,
  EXPLORE_PHOTO_ALBUM_CAPTION_HEIGHT
} from 'dpl/constants/photo_album';
import { scrollTo, chunk } from 'dpl/shared/utils';
import { get } from 'dpl/shared/utils/object';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import SmartImage from 'dpl/components/SmartImage';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import ZoomingPhoto from 'dpl/components/ZoomingPhoto';
import Icon from 'dpl/common/components/Icon';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';

import ExploreBreedCategories from './BreedCategories';
import ExplorePagePhotoAlbumContainer from '../containers/PhotoAlbumContainer';
import ExploreRelatedBreedCategoriesContainer from '../containers/RelatedBreedCategoriesContainer';

const IS_MOBILE = isLessThanBreakpoint('md');
const TILES_PER_ROW = IS_MOBILE ? 9 : 10;
const LAZY_IMAGE_BUFFER = window.outerHeight / 2;

export const HEADER_HEIGHT = 66;

export default class ExplorePage extends Component {
  static propTypes = {
    intersectingElement: PropTypes.instanceOf(window.HTMLElement),
    intersectingElements: PropTypes.arrayOf(
      PropTypes.instanceOf(window.HTMLElement)
    ).isRequired,
    displayedImageId: PropTypes.number,
    onImageClick: PropTypes.func.isRequired,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        alt_text: PropTypes.string,
        url: PropTypes.string.isRequired
      })
    ).isRequired,
    breedCategories: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        cover_photo_url: PropTypes.string.isRequired
      })
    ).isRequired,
    breedsForCategory: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        cover_photo_url: PropTypes.string.isRequired
      })
    ).isRequired,
    breedsData: PropTypes.shape({
      meta: PropTypes.shape({ isSuccess: PropTypes.bool })
    }).isRequired,
    breedPhotosData: PropTypes.shape({
      meta: PropTypes.shape({ isSuccess: PropTypes.bool })
    }).isRequired,
    setIntersectingElement: PropTypes.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    photosByBreed: PropTypes.object.isRequired,
    breedCategorySlug: PropTypes.string.isRequired,
    breedSlug: PropTypes.string.isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired
    }).isRequired
  };

  static defaultProps = {
    intersectingElement: null,
    displayedImageId: null
  };

  breedLinkRef = createRef();

  redirectToFirstBreedIfNeeded() {
    const { breedCategories, breedsForCategory, breedCategorySlug, breedSlug } =
      this.props;

    if (!breedCategorySlug && breedCategories[0]) {
      this.navigate('', breedCategories[0].slug);
      return true;
    }

    if (!breedSlug && breedsForCategory[0]) {
      this.navigate(breedsForCategory[0].slug);
      return true;
    }

    return false;
  }

  trackResultsClick = breedName => {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: `${breedName} breeders`,
      view: ANALYTICS_VIEWS.EXPLORE_PAGE,
      view_feature: ANALYTICS_VIEW_FEATURES.EXPLORE_PAGE_RESULTS
    });
  };

  handleBreedLinkClick = (breedName, breedSlug) => {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: breedName,
      view: ANALYTICS_VIEWS.EXPLORE_PAGE,
      view_feature: ANALYTICS_VIEW_FEATURES.EXPLORE_PAGE_BREED
    });

    this.navigate(breedSlug);
    this.scrollToElement(breedSlug);
  };

  navigate(breedSlug, breedCategorySlug) {
    if (!breedCategorySlug) {
      ({ breedCategorySlug } = this.props);
    }

    this.props.history.replace(`/explore/${breedCategorySlug}/${breedSlug}`);
  }

  scrollToElement(breedSlug) {
    const { intersectingElements } = this.props;

    const scrollToEl = intersectingElements.find(
      el => el.dataset.href === breedSlug
    );
    if (scrollToEl) {
      scrollTo({
        top: scrollToEl.offsetTop - HEADER_HEIGHT + 1
      });
    }
  }

  componentDidMount() {
    this.redirectToFirstBreedIfNeeded();
    this.scrollToElement(this.props.breedSlug);
  }

  componentDidUpdate(prevProps) {
    const { intersectingElement, photos, breedSlug, breedsForCategory } =
      this.props;

    const didRedirect = this.redirectToFirstBreedIfNeeded();

    if (didRedirect || !breedSlug) {
      return;
    }

    if (
      // initial load; scroll to slug user navigated to
      prevProps.photos.length === 0 &&
      photos.length > 0 &&
      // don't scroll if first breed
      get(breedsForCategory, '0.slug', breedSlug) !== breedSlug
    ) {
      this.scrollToElement(breedSlug);
      this.breedLinkRef.current.scrollIntoView();
      return;
    }

    // breed changed
    if (prevProps.breedSlug && prevProps.breedSlug !== breedSlug) {
      this.breedLinkRef.current.scrollIntoView();
      return;
    }

    // user scrolled to new breed
    if (
      intersectingElement &&
      intersectingElement !== prevProps.intersectingElement
    ) {
      this.navigate(intersectingElement.dataset.href);
    }
  }

  render() {
    let tileRowIdx = -1;

    const {
      displayedImageId,
      onImageClick,
      photos,
      breedCategories,
      breedsForCategory,
      breedPhotosData,
      breedsData,
      setIntersectingElement,
      breedCategorySlug,
      breedSlug,
      photosByBreed
    } = this.props;

    return (
      <div className="ExplorePage center pt8 pt10-md">
        <h1 className="mb8 lh-1 f5 f8-sm tc">Explore Breeds</h1>
        <ExploreBreedCategories
          categories={breedCategories}
          selectedSlug={breedCategorySlug}
          hideArrows
        />

        <div className="sticky z-2 bg-white top-0 left-0 right-0 nh3 ph3">
          <div className="row slim-gutters flex-nowrap overflow-x-auto no-scrollbars">
            {breedsForCategory.map(breed => (
              <div
                className="col-auto"
                key={breed.slug}
                ref={breed.slug === breedSlug ? this.breedLinkRef : null}
              >
                <ExploreBreedLink
                  breedCategorySlug={breedCategorySlug}
                  breed={breed}
                  isSelected={breed.slug === breedSlug}
                  onClick={() =>
                    this.handleBreedLinkClick(breed.name, breed.slug)
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div className="ExplorePage__content pv7-md pv1 nh3">
          <LoadingWrapper
            isLoading={
              !breedPhotosData.meta.isSuccess || !breedsData.meta.isSuccess
            }
          >
            {breedsForCategory.map((breed, idx) => {
              const photoTiles = (photosByBreed[breed.slug] || []).map(p => (
                /* eslint-disable-next-line react/jsx-key */
                <div className="absolute absolute--fill">
                  <ZoomingPhoto
                    onClick={onImageClick}
                    isDisplayed={p.id === displayedImageId}
                    image={{ url: p.url, id: p.id }}
                    smartImageProps={{
                      lazyBuffer: LAZY_IMAGE_BUFFER
                    }}
                    albumPadding={{
                      top: PHOTO_ALBUM_VERTICAL_PADDING / 2,
                      bottom:
                        EXPLORE_PHOTO_ALBUM_CAPTION_HEIGHT +
                        PHOTO_ALBUM_VERTICAL_PADDING / 2,
                      right: 0
                    }}
                  />
                </div>
              ));

              const detailsTile = <ExploreBreedDetailsTile breed={breed} />;

              let allTiles = [detailsTile, ...photoTiles];

              // re-render first photos if row is not full
              while (
                photoTiles.length &&
                allTiles.length % TILES_PER_ROW !== 0
              ) {
                allTiles = [
                  ...allTiles,
                  ...photoTiles.slice(
                    0,
                    TILES_PER_ROW - (allTiles.length % TILES_PER_ROW)
                  )
                ];
              }

              if (IS_MOBILE) {
                const breedersLinkTile = (
                  <a
                    href={`/${breed.slug}`}
                    onClick={() => this.trackResultsClick(breed.name)}
                    className="absolute absolute--fill bg-white fw-medium f2 flex flex-column justify-center tc ph2 overflow-hidden"
                  >
                    <p>{breed.name}</p>
                    <p>Breeders</p>
                    <p className="f5 lh-1">&rarr;</p>
                  </a>
                );

                allTiles[allTiles.length - 1] = breedersLinkTile;
              }

              allTiles = allTiles.map((t, _idx) =>
                cloneElement(t, { key: _idx })
              );

              return (
                <div
                  key={breed.slug}
                  ref={el => setIntersectingElement(idx, el)}
                  data-href={breed.slug}
                >
                  {chunk(allTiles, TILES_PER_ROW).map(tiles => {
                    tileRowIdx++;

                    return (
                      <ExploreGridRow
                        key={tileRowIdx}
                        position={tileRowIdx % 2 ? 'right' : 'left'}
                        subPosition={
                          IS_MOBILE || Math.floor(tileRowIdx / 2) % 2
                            ? 'left'
                            : 'right'
                        }
                      >
                        {tiles}
                      </ExploreGridRow>
                    );
                  })}
                </div>
              );
            })}
          </LoadingWrapper>
        </div>
        <button
          type="button"
          className="ExplorePage__scrollToTopButton db tc sticky bottom-1 mr2 z-1 bg-white"
          onClick={() => scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <Icon
            className="ph1 pv1 ba bw2"
            height="40px"
            name="lined-arrow-up"
            width="40px"
          />
        </button>

        {breedCategorySlug && (
          <ExploreRelatedBreedCategoriesContainer
            breedCategorySlug={breedCategorySlug}
          />
        )}

        <ExplorePagePhotoAlbumContainer images={photos} />
      </div>
    );
  }
}

function ExploreGridTile({ children, className }) {
  return (
    <div className={classnames('ExploreGridTile', className)}>
      <div className="aspect-ratio aspect-ratio--1x1 bg-near-white">
        {children}
      </div>
    </div>
  );
}

ExploreGridTile.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

ExploreGridTile.defaultProps = {
  className: null
};

function ExploreGridRow({ children, position, subPosition }) {
  return (
    <div
      className={classnames('ExploreGridRow row no-gutters f0', {
        'flex-row-reverse': position === 'right'
      })}
    >
      <div className="col-md-6">
        <div
          className={classnames('row no-gutters', {
            'flex-row-reverse': subPosition === 'right'
          })}
        >
          <ExploreGridTile className="col-8">{children[0]}</ExploreGridTile>
          <div className="col-4">
            {children.slice(1, 3).map((child, idx) => (
              <ExploreGridTile key={idx}>{child}</ExploreGridTile>
            ))}
          </div>
        </div>
        <div className="row no-gutters">
          {children.slice(3, 6).map((child, idx) => (
            <ExploreGridTile key={idx} className="col-4">
              {child}
            </ExploreGridTile>
          ))}
        </div>
      </div>

      <div className="col-md-6">
        <div className="row no-gutters">
          {children.slice(6, 10).map((child, idx) => (
            <ExploreGridTile className="col-md-6 col-4" key={idx}>
              {child}
            </ExploreGridTile>
          ))}
        </div>
      </div>
    </div>
  );
}

ExploreGridRow.propTypes = {
  children: PropTypes.node.isRequired,
  position: PropTypes.oneOf(['left', 'right']).isRequired,
  subPosition: PropTypes.oneOf(['left', 'right']).isRequired
};

function ExploreBreedLink({ breed, isSelected, onClick }) {
  return (
    <a
      onClick={e => {
        e.preventDefault();
        onClick(e);
      }}
      className={classnames(
        'br2 mv2 card ba bw1 overflow-hidden b--light-gray flex items-center',
        {
          'b--stone-700': isSelected,
          'b--transparent': !isSelected
        }
      )}
      role="button"
    >
      <SmartImage lazy src={breed.cover_photo_url} width="46px" height="46px" />
      <p className="ph3 f2 fw-medium">{breed.name}</p>
    </a>
  );
}

ExploreBreedLink.propTypes = {
  breed: PropTypes.shape({
    cover_photo_url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

function ExploreBreedDetailsTileContent({ children, className, breed }) {
  return (
    <div
      className={classnames(
        className,
        'ExploreBreedDetailsTile__side bg-black absolute absolute--fill f3'
      )}
    >
      <div className="ExploreBreedDetailsTile__content h-100 flex flex-column">
        <div className="pv8-lg ph8-lg pv12-xl ph12-xl ph6-md pv6-md ph4 pv4 flex-auto">
          {children}
        </div>
        <p className="light-gray pb6-md pb3 pt2 ph6-md ph3 flex items-center justify-end">
          <span className="d-none d-md-block">flip</span>
          <Icon className="ml2" name="refresh" />
        </p>
        <a
          onClick={e => e.stopPropagation()}
          href={breed.url}
          className="pv2 pv3-md pv5-lg button button--large bg-white black w-100 bw2 f4-lg fw-medium"
        >
          View Breeders
        </a>
      </div>
    </div>
  );
}

ExploreBreedDetailsTileContent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  breed: PropTypes.shape({
    url: PropTypes.string.isRequired
  }).isRequired
};

ExploreBreedDetailsTileContent.defaultProps = {
  className: null
};

function _ExploreBreedDetailsTile({ toggle, isOn, breed }) {
  return (
    <div className="ExploreBreedDetailsTile z-1 absolute absolute--fill overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        className={classnames(
          'ExploreBreedDetailsTile__card absolute absolute--fill w-100 white f0',
          {
            'ExploreBreedDetailsTile__card--flipped': isOn
          }
        )}
      >
        <ExploreBreedDetailsTileContent breed={breed}>
          <p className="mb8-xl mb4-lg mb2 fw-medium f7-xl f5-lg f4">
            {breed.name}
          </p>
          <ul className="f4-xl f3-lg f2 fw-medium">
            {[breed.sizes.join(', '), ...breed.traits].map((text, idx) => (
              <li
                key={idx}
                className={classnames('mb6-xl mb2', {
                  // don't render allergy friendliness on mobile
                  'd-none d-lg-block': idx === 1
                })}
              >
                {text}
              </li>
            ))}
          </ul>
        </ExploreBreedDetailsTileContent>
        <ExploreBreedDetailsTileContent
          breed={breed}
          className="ExploreBreedDetailsTile__backSide"
        >
          <p className="mb8-xl mb2 f4-xl f3-md f2 fw-medium tc">Fun fact:</p>
          <p className="f4-xl f3-md f2 fw-medium tc">{breed.fun_fact}</p>
        </ExploreBreedDetailsTileContent>
      </button>
    </div>
  );
}

_ExploreBreedDetailsTile.propTypes = {
  isOn: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  breed: PropTypes.shape({
    traits: PropTypes.arrayOf(PropTypes.string).isRequired,
    sizes: PropTypes.arrayOf(PropTypes.string).isRequired,
    fun_fact: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  }).isRequired
};

const ExploreBreedDetailsTile = withToggle()(_ExploreBreedDetailsTile);
